import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Alert, Box, Button, CircularProgress } from "@mui/material";
import { Add, Delete, Edit, Visibility } from "@mui/icons-material";

import { DialogBox, Drawer, DrawerControl, MetaDrivenTable, PaginationControls, SearchField, StatusChip } from "../../../shared/components";
import { useGetMaterialReq } from "../hooks/useGetMaterialReq";
import { AddRequest, DeleteConfirmation, RequestDetails, RequestDetailsHeader } from "../components";
import { useDeleteRequest } from "../hooks/useDeleteRequest";
import { useCapabilities } from "../../../hooks/useCapabilities";


export const MaterialRequests = () => {

    const { projectId } = useParams();

    const [searchParams, setSearchParams] = useSearchParams();
    const [newRequest, setNewRequest] = useState(false);

    const { isAllowedToAddMaterialRequest, isAllowedToEditMaterialRequest, isAllowedToDeleteMaterialRequest } = useCapabilities();

    const openDetailsDrawer = searchParams.get("details") === "true";
    const openEditDrawer = searchParams.get("edit") === "true";
    const project_id = searchParams.get("projectId") || null;
    const requestId = searchParams.get("requestId") || null;

    const handleOpenDetailsDrawer = (requestId: string) => {
        searchParams.set("details", "true");
        searchParams.set("requestId", requestId);
        setSearchParams(searchParams, { replace: true });
    }

    const handleOpenEditDrawer = (requestId: string) => {
        searchParams.set("edit", "true");
        searchParams.set("requestId", requestId);
        setSearchParams(searchParams, { replace: true });
    }

    const handleCloseDrawers = () => {
        searchParams.delete("details");
        searchParams.delete("edit");
        searchParams.delete("requestId");
        setSearchParams(searchParams, { replace: true });
    }

    const [deleteDetails, setDeleteDetails] = useState<{
        status: boolean,
        requestId: string | null
    }>({
        status: false,
        requestId: null
    });

    const {
        data, isFetching, isPending, isError, error, searchDraft, pagination,
        refetch, changePage, changeRowsPerPage, handleChangeSearch, handleClear, handleSearch
    } = useGetMaterialReq({ projectId: projectId });

    const {
        deleting,
        deleteError,
        handleDelete
    } = useDeleteRequest({
        onSuccess: () => {
            setDeleteDetails({ status: false, requestId: null });
            refetch();
        }
    });

    const headers = [
        { "alias_name": "material_ref", "label": "Material REF", "display_order": 1, is_show: true },
        { "alias_name": "type_display_text", "label": "Type", "display_order": 2, is_show: true },
        { "alias_name": "location", "label": "Location", "display_order": 3, is_show: true },
        { "alias_name": "point_of_contact_info", "label": "Point of Contact Info", "display_order": 6, is_show: true },
        { "alias_name": "status_display_text", "label": "Status", "display_order": 7, is_show: true },
        { "alias_name": "supplier_display_text", "label": "Supplier", "display_order": 8, is_show: true },
        { "alias_name": "required_date", "label": "Required Date", "display_order": 9, is_show: true },
        { "alias_name": "po_number", "label": "PO Number", "display_order": 10, is_show: true },
        { "alias_name": "date_po_raised", "label": "Date PO Raised", "display_order": 11, is_show: true },
        { "alias_name": "last_updated_comments", "label": "Last Updated Comments", "display_order": 12, is_show: true },
        { "alias_name": "activity_name", "label": "Activity Name", "display_order": 13, is_show: true },
        { "alias_name": "owner_name", "label": "Owner Name", "display_order": 14, is_show: true },
    ];

    const metrics = {
        status_display_text: {
            type: 'component',
            element: ((row: { status_display_text: string; }) => (
                <StatusChip label={row.status_display_text} color={
                    row.status_display_text === 'Requested' ? 'default' :
                        row.status_display_text === 'Approved' ? 'blue' :
                            row.status_display_text === 'Ordered' ? 'amber' :
                                row.status_display_text === 'Delivered' ? 'green' :
                                    row.status_display_text === 'Used' ? 'violet' :
                                        'default'
                } />
            ))
        },
        order_placed_date: {
            type: 'date',
        },
        est_delivery_date: {
            type: 'date',
        },
        required_date: {
            type: 'date',
        },
        date_po_raised: {
            type: 'date',
        }
        
    };

    if (isPending || isFetching) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    if (isError) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
            >
                <Alert
                    severity="warning"
                    sx={{
                        width: "auto",
                        justifyContent: "center",
                        textAlign: "center",
                    }}
                >
                    {error?.message || "Something went wrong!"}
                </Alert>
            </Box>
        );
    };

    return (
        <Box height={'100%'} display={'flex'} flexDirection={'column'}>

            <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} gap={2} px={2}>
                <SearchField searchTerm={searchDraft} handleSearch={handleChangeSearch} handleClearSearch={handleClear} handleApplySearch={handleSearch} />
                {isAllowedToAddMaterialRequest && <Box display={'flex'} alignItems={'center'} py={2} gap={2}>
                    <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => setNewRequest(true)}>NEW MATERIAL REQUEST</Button>
                </Box>}
            </Box>

            <Box flex={1}>
                <MetaDrivenTable headers={headers} data={data} dataMetrics={metrics} sortDisabled
                    actions={[

                         {
                            label: <Visibility sx={{ fontSize: 18 }} />,
                            handleClick: (row: { id: string }) => {
                                handleOpenDetailsDrawer(row.id);
                            }
                        },
                        {
                            label: isAllowedToEditMaterialRequest && <Edit sx={{ fontSize: 18 }} />,
                            handleClick: (row: { id: string }) => {
                                handleOpenEditDrawer(row.id);
                            }
                        },
                        {
                            label: isAllowedToDeleteMaterialRequest && <Delete sx={{ fontSize: 18 }} />,
                            handleClick: (row: { id: string }) => {
                                setDeleteDetails({
                                    status: true,
                                    requestId: row.id,
                                });
                            }
                        },
                       
                    ]}
                />
            </Box>

            <Box flexShrink={0}>
                <PaginationControls
                    total={pagination?.total}
                    rowsPerPage={pagination?.limit}
                    currentPage={pagination?.page}
                    handlePageChange={changePage}
                    handleRowsPerPage={changeRowsPerPage}
                />
            </Box>

            <DrawerControl
                title={openEditDrawer ? "Edit Material Request" : "New Material Request"}
                subTitle={""}
                isOpen={newRequest || openEditDrawer}
                handleClose={() => {
                    setNewRequest(false);
                    handleCloseDrawers();
                }}
                children={
                    <AddRequest
                        projectId={projectId}
                        refetch={refetch}
                        requestId={requestId}
                        isEdit={openEditDrawer}
                        onSuccess={function (): void {
                            setNewRequest(false);
                            handleCloseDrawers();
                        }}
                        onClose={() => {
                            setNewRequest(false);
                            handleCloseDrawers();
                        }}
                    />
                }
            />

            <Drawer
                isOpen={openDetailsDrawer}
                header={
                    <RequestDetailsHeader
                        onClose={() => { 
                            handleCloseDrawers();
                        }}
                        projectId={projectId}
                        requestId={requestId}
                    />
                }
                children={
                    <RequestDetails
                        projectId={projectId}
                        requestId={requestId}
                    />
                }
            />

            <DialogBox
                showModal={deleteDetails.status}
                title={"Delete Material Request"}
                subTitle="Are you sure you want to delete this material request? This action cannot be undone."
                onModalClose={() => {
                    setDeleteDetails({ status: false, requestId: null })
                }}
                children={
                    <DeleteConfirmation
                        deleting={deleting}
                        onConfirm={() => {
                            handleDelete({ requestId: deleteDetails.requestId });
                        }}
                        onCancel={() => {
                            setDeleteDetails({ status: false, requestId: null });
                        }}
                    />
                }
            />

        </Box>
    )
}